import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LockOpen } from 'lucide-react';
import { useAppSettings } from '../providers/AppSettingsProvider';
import { INSIGHT_EMOTIONS } from '../../config/insightEmotions';
import {
  fetchExploreInsights,
  type ExploreInsightItem,
} from '../../services/exploreService';

type LibraryKnowYourselfContentProps = {
  isDark: boolean;
};

function emotionLabelFromInsightType(
  t: (key: string, params?: Record<string, string>) => string,
  insightType: string,
): string {
  const key = `library.knowYourself.emotion.${insightType}`;
  const localized = t(key);
  if (localized !== key) return localized;
  return insightType
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const LibraryKnowYourselfContent: React.FC<LibraryKnowYourselfContentProps> = ({ isDark }) => {
  const { t } = useAppSettings();
  const navigate = useNavigate();
  const [apiItems, setApiItems] = useState<ExploreInsightItem[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await fetchExploreInsights();
      if (cancelled) return;
      if (data?.items?.length) {
        setApiItems(data.items);
      } else {
        setApiItems(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fallbackRows = useMemo(
    () => INSIGHT_EMOTIONS.map((em) => ({ kind: 'local' as const, id: em.id })),
    [],
  );

  const rows = useMemo(() => {
    if (apiItems?.length) {
      const readyFirst = [...apiItems].sort((a, b) => {
        const rank = (s: ExploreInsightItem['status']) => (s === 'ready' ? 0 : 1);
        return rank(a.status) - rank(b.status);
      });
      return readyFirst.map((item) => ({ kind: 'api' as const, item }));
    }
    return fallbackRows;
  }, [apiItems, fallbackRows]);

  const whatMakesMe = t('library.knowYourself.insights.whatMakesMe');

  return (
    <div className="space-y-6">
      <div>
        <h2
          className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
        >
          {t('library.knowYourself.insights.title')}
        </h2>
        <p
          className={`mt-2 text-sm font-normal leading-relaxed ${
            isDark ? 'text-white/85' : 'text-gray-600'
          }`}
        >
          {t('library.knowYourself.insights.subtitle')}
        </p>
      </div>

      {loading ? (
        <div
          className="grid grid-cols-2 gap-3"
          aria-busy="true"
          aria-label={t('library.knowYourself.insights.title')}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`min-h-[6.5rem] animate-pulse rounded-2xl ${
                isDark ? 'bg-[#1a4882]/70' : 'bg-blue-100/90'
              }`}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {rows.map((row) => {
            if (row.kind === 'local') {
              const id = row.id;
              const label = emotionLabelFromInsightType(t, id);
              const aria = `${label}, ${t('library.knowYourself.insights.locked')}`;
              const cardSurface = isDark
                ? 'bg-[#235a9a]'
                : 'bg-sky-100/95 border border-sky-200/90';
              const line1Cls = isDark ? 'text-white/90' : 'text-gray-600';
              const line2Cls = isDark ? 'text-white' : 'text-gray-900';
              const lockCls = isDark ? 'text-white' : 'text-gray-500';

              return (
                <button
                  key={id}
                  type="button"
                  aria-label={aria}
                  onClick={() => navigate('/insight-locked', { state: { emotionId: id } })}
                  className={`relative flex min-h-[5rem] w-full flex-col rounded-2xl px-3 pb-2 pt-2 text-center transition-opacity hover:opacity-95 active:opacity-90 ${cardSurface}`}
                >
                  <div className="flex flex-1 flex-col items-center justify-center">
                    <span className={`text-sm font-normal leading-tight ${line1Cls}`}>
                      {whatMakesMe}
                    </span>
                    <span className={`mt-1.5 text-lg font-bold leading-snug ${line2Cls}`}>{label}</span>
                  </div>
                  <Lock
                    className={`absolute bottom-3 right-3 h-[1.05rem] w-[1.05rem] ${lockCls}`}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
              );
            }

            const item = row.item;
            const isUnlocked = item.status === 'ready';
            const label = emotionLabelFromInsightType(t, item.insight_type);
            const aria = isUnlocked
              ? `${label}, ${t('library.knowYourself.insights.unlocked')}`
              : `${label}, ${t('library.knowYourself.insights.locked')}`;

            const cardSurface = isUnlocked
              ? isDark
                ? 'bg-gradient-to-br from-[#2f6fc9] via-[#3d84d9] to-[#52a0ef] shadow-md shadow-black/25'
                : 'bg-gradient-to-br from-sky-500 to-blue-600 shadow-md shadow-sky-900/15'
              : isDark
                ? 'bg-[#235a9a]'
                : 'bg-sky-100/95 border border-sky-200/90';

            const line1Cls = isUnlocked
              ? 'text-white/95'
              : isDark
                ? 'text-white/90'
                : 'text-gray-600';
            const line2Cls = isUnlocked
              ? 'text-white'
              : isDark
                ? 'text-white'
                : 'text-gray-900';

            const inner = (
              <>
                <div className="flex flex-1 flex-col items-center justify-center">
                  <span className={`text-sm font-normal leading-tight ${line1Cls}`}>
                    {whatMakesMe}
                  </span>
                  <span className={`mt-1.5 text-lg font-bold leading-snug ${line2Cls}`}>{label}</span>
                </div>
                {isUnlocked ? (
                  <LockOpen
                    className="absolute bottom-3 right-3 h-[1.05rem] w-[1.05rem] text-[#f5d658]"
                    strokeWidth={2}
                    aria-hidden
                  />
                ) : (
                  <Lock
                    className={`absolute bottom-3 right-3 h-[1.05rem] w-[1.05rem] ${
                      isDark ? 'text-white' : 'text-gray-500'
                    }`}
                    strokeWidth={2}
                    aria-hidden
                  />
                )}
              </>
            );

            const shellCls = `relative flex min-h-[6.5rem] w-full flex-col rounded-2xl px-3 pb-4 pt-4 text-center ${cardSurface}`;

            if (isUnlocked) {
              return (
                <button
                  key={item.insight_type}
                  type="button"
                  aria-label={aria}
                  onClick={() => navigate('/insight', { state: { item } })}
                  className={`${shellCls} transition-opacity hover:opacity-95 active:opacity-90`}
                >
                  {inner}
                </button>
              );
            }

            return (
              <button
                key={item.insight_type}
                type="button"
                aria-label={aria}
                onClick={() =>
                  navigate('/insight-locked', { state: { emotionId: item.insight_type } })
                }
                className={`${shellCls} transition-opacity hover:opacity-95 active:opacity-90`}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LibraryKnowYourselfContent;
